import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { EnumTotemType } from '@/pages/StoreManagement/states';
import { MonthlyFee } from '../Billing/types';

export const QUERY_STORE_MANAGEMENT = 'QUERY_STORE_MANAGEMENT';

export type InventoryType =
    | 'store-physical'
    | 'warehouse-physical'
    | 'virtual'
    | 'null';

export enum StoreListOrderBy {
  first_sale = 'firstSale',
  last_sale = 'lastSale',
  sum_total_one_month_ago = 'salesLastMonth',
}

export enum StoreListOrderDir {
    ascend = 'asc',
    descend = 'desc'
}

export interface ParamsStoreManagement {
    page?: number;
    perPage?: number;
    name?: string;
    shopmanagerId?: number;
    storeId?: number;
    shopmanagerEmail?: string;
    isTotemAuthenticated?: boolean | null;
    totemEnum?: EnumTotemType,
    city?: string;
    state?: string;
    totem?: boolean;
    active?: boolean;
    open?: boolean;
    inventory?: InventoryType[];
    firstSaleDate?: string;
    saleInLastSevenDays?: boolean | string;
    orderBy?: StoreListOrderBy;
    orderDir?: StoreListOrderDir
}

export interface Summary {
    stores: number;
    monthMinusOne: number;
    monthMinusTwo: number;
    variation: number;
}

export interface Meta {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    first_page: number;
    first_page_url: string;
    last_page_url: string;
    next_page_url: string;
    previous_page_url: any;
}

export interface StoreManagement {
    id: number;
    shopmanager_id: number;
    address_id: number;
    name: string;
    category: string;
    logo: string;
    latitude: number;
    longitude: number;
    is_open: number;
    description?: any;
    type?: any;
    phone?: any;
    validUntil: string | null;
    inventory: string;
    created_at: string;
    updated_at: string;
    qr_code?: any;
    active: number;
    is_qr_code_required: number;
    fraud_prevention_id: number;
    store_business_type: string;
    store_business_quantity: number;
    hide_zero_stock: number;
    active_pwa: number;
    active_app: number;
    oneMonthAgoFinancialReport: MonthAgoFinancialReport;
    twoMonthAgoFinancialReport: MonthAgoFinancialReport;
    threeMonthAgoFinancialReport: MonthAgoFinancialReport;
    sixMonthAgoFinancialReport: MonthAgoFinancialReport;
    fourMonthAgoFinancialReport: MonthAgoFinancialReport;
    fiveMonthAgoFinancialReport: MonthAgoFinancialReport;
    allow_purchases_stock_less_than_quantity: number;
    automatic_refund: number;
    automatic_refund_value: number;
    sum_total_two_months_ago: string;
    sum_total_one_month_ago: string;
    first_sale: string;
    last_sale: string;
    address: Address;
    shopManager: ShopManager;
    paymentsStore: PaymentsStore[];
    totemCount: number;
    lockCount: number;
    can_emit_invoice: number;
    is_aux_mode_enabled: number;
}

export interface MonthAgoFinancialReport {
    automatic_monthly_fee: number;
    monthly_invoice: number;
    store_id: number;
    reference_date: string;
}

export interface PaymentsStore {
    payment_method_id: number;
    is_active: number;
    store_id: number;
    paymentMethod: PaymentMethod;
}
export interface PaymentMethod {
    name: string;
    id: number;
}
export interface ShopManager {
    id: number;
    name: string;
    user_id: number;
    active_app: number;
    is_blocked: number;
    active_pwa: number;
    emit_invoice: number;
    percentage: string;
    plugpag_email: string | null;
    doc: string | null;
    pagbank_split_percentage: string;
    monthly_fee_type: MonthlyFee;
    monthly_fee_value: string;
    user: User;
}
export interface User {
    email: string;
    id: number;
}

export interface Address {
    city: string;
    state: string;
    id: number;
}

export interface ShopManagerResponse {
    meta: Meta;
    data: StoreManagement[];
    summary: Summary;
}

export function useStoreManagement(
    params?: ParamsStoreManagement,
    enable: boolean = true,
) {
    const getAllStoreManagement = async () => {
        const response = await api.get<ShopManagerResponse>(
            '/app/v2/stores/getStores',
            { params },
        );

        return response.data;
    };

    return useQuery([QUERY_STORE_MANAGEMENT, params], getAllStoreManagement, {
        retry: false,
        enabled: !!enable,
    });
}
